import React, { useState } from 'react';
import {
  surfaces, textColors, typography, borders,
} from '../../tokens';
import {
  Input,
  TextArea,
  Select,
  Checkbox,
  Radio,
  RadioGroup,
  Toggle,
  SearchInput,
  FormField,
} from '../../components/inputs';
import { SearchIcon, MessageIcon } from '../../icons';

/**
 * InputShowcase
 * Displays all form input components with interactive examples
 */
const InputShowcase = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    password: '',
    bio: '',
    country: '',
    terms: false,
    notifications: true,
    theme: 'light',
    search: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateEmail = () => {
    if (formState.email && !formState.email.includes('@')) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    }
  };

  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  return (
    <div className="space-y-12">
      {/* Input Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">Input</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Single-line text inputs for various data types.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Sizes</h3>
          <div className="space-y-3 p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <Input 
              size="sm" 
              placeholder="Small input" 
              value={formState.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <Input 
              size="md" 
              placeholder="Medium input (default)" 
              value={formState.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <Input 
              size="lg" 
              placeholder="Large input" 
              value={formState.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">States</h3>
          <div className="space-y-3 p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <Input placeholder="Normal state" />
            <Input 
              placeholder="Enter email" 
              value={formState.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={validateEmail}
              error={errors.email}
            />
            <Input placeholder="Disabled state" disabled />
            <Input type="password" placeholder="Password input" />
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">With Icons</h3>
          <div className="space-y-3 p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <Input 
              placeholder="Search..." 
              leftIcon={<SearchIcon size="sm" />}
            />
            <Input 
              placeholder="Email address" 
              leftIcon={<MessageIcon size="sm" />}
              type="email"
            />
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { Input } from '../design-system';

<Input
  type="email"
  value={email}
  onChange={(e) => setEmail(e.target.value)}
  placeholder="Enter email"
  error={errors.email}
  leftIcon={<MessageIcon />}
/>`} />
        </div>
      </section>

      {/* TextArea Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">TextArea</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Multi-line text inputs with auto-resize capability.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Interactive Example</h3>
          <div className="p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <TextArea
              placeholder="Enter your bio..."
              value={formState.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              rows={4}
            />
            <p className="mt-2 text-sm text-gray-500">{formState.bio.length} characters</p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { TextArea } from '../design-system';

<TextArea
  placeholder="Enter description..."
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  rows={4}
/>`} />
        </div>
      </section>

      {/* Select Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">Select</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Dropdown select menus with native styling.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Interactive Example</h3>
          <div className="space-y-3 p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <Select
              value={formState.country}
              onChange={(e) => handleChange('country', e.target.value)}
            >
              <option value="">Select a country</option>
              <option value="us">United States</option>
              <option value="uk">United Kingdom</option>
              <option value="ca">Canada</option>
              <option value="au">Australia</option>
            </Select>
            <Select size="sm">
              <option>Small select</option>
            </Select>
            <Select size="lg">
              <option>Large select</option>
            </Select>
            <Select disabled>
              <option>Disabled select</option>
            </Select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { Select } from '../design-system';

<Select value={country} onChange={handleChange}>
  <option value="">Select country</option>
  <option value="us">United States</option>
  <option value="uk">United Kingdom</option>
</Select>`} />
        </div>
      </section>

      {/* Checkbox Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">Checkbox</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Toggle controls with labels for boolean choices.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Interactive Example</h3>
          <div className="space-y-3 p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <Checkbox
              checked={formState.terms}
              onChange={(e) => handleChange('terms', e.target.checked)}
              label="I agree to the terms and conditions"
            />
            <Checkbox
              checked={false}
              onChange={() => {}}
              label="Unchecked checkbox"
            />
            <Checkbox
              checked={true}
              disabled
              label="Disabled checked"
            />
            <Checkbox
              checked={false}
              disabled
              label="Disabled unchecked"
            />
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { Checkbox } from '../design-system';

<Checkbox
  checked={agreed}
  onChange={(e) => setAgreed(e.target.checked)}
  label="I agree to terms"
/>`} />
        </div>
      </section>

      {/* Radio Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">Radio</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Single selection from multiple options with RadioGroup.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Interactive Example</h3>
          <div className="p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <RadioGroup
              value={formState.theme}
              onChange={(value) => handleChange('theme', value)}
              label="Choose theme"
            >
              <Radio value="light" label="Light theme" />
              <Radio value="dark" label="Dark theme" />
              <Radio value="system" label="System default" />
            </RadioGroup>
            
            <p className="mt-4 text-sm text-gray-600">
              Selected: <span className="font-medium">{formState.theme}</span>
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { RadioGroup, Radio } from '../design-system';

<RadioGroup
  value={theme}
  onChange={setTheme}
  label="Choose theme"
>
  <Radio value="light" label="Light theme" />
  <Radio value="dark" label="Dark theme" />
  <Radio value="system" label="System default" />
</RadioGroup>`} />
        </div>
      </section>

      {/* Toggle Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">Toggle</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Switch-style boolean controls for settings.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Interactive Example</h3>
          <div className="space-y-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <Toggle
              checked={formState.notifications}
              onChange={(e) => handleChange('notifications', e.target.checked)}
              label="Enable notifications"
            />
            <Toggle
              checked={false}
              onChange={() => {}}
              label="Unchecked toggle"
            />
            <Toggle
              checked={true}
              disabled
              label="Disabled checked"
            />
            <Toggle
              checked={false}
              disabled
              label="Disabled unchecked"
            />
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { Toggle } from '../design-system';

<Toggle
  checked={enabled}
  onChange={(e) => setEnabled(e.target.checked)}
  label="Enable feature"
/>`} />
        </div>
      </section>

      {/* SearchInput Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">SearchInput</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Input with search icon and clear button functionality.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Interactive Example</h3>
          <div className="p-6 ${surfaces.primary} rounded-lg border ${borders.default}">
            <SearchInput
              value={formState.search}
              onChange={(e) => handleChange('search', e.target.value)}
              onClear={() => handleChange('search', '')}
              placeholder="Search..."
            />
            {formState.search && (
              <p className="mt-2 text-sm text-gray-600">
                Searching for: <span className="font-medium">{formState.search}</span>
              </p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { SearchInput } from '../design-system';

<SearchInput
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  onClear={() => setSearchTerm('')}
  placeholder="Search..."
/>`} />
        </div>
      </section>

      {/* FormField Component */}
      <section>
        <h2 className="${typography.h2} ${textColors.bright} mb-4">FormField</h2>
        <p className="${typography.body} ${textColors.secondary} mb-6">
          Wrapper component with labels, help text, and error messages.
        </p>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Complete Form Example</h3>
          <div className="space-y-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default} max-w-2xl">
            <FormField
              label="Full Name"
              helperText="Enter your first and last name"
              required
            >
              <Input 
                placeholder="John Doe"
                value={formState.name}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </FormField>

            <FormField
              label="Email Address"
              error={errors.email}
              required
            >
              <Input 
                type="email"
                placeholder="john@example.com"
                value={formState.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={validateEmail}
                error={errors.email}
              />
            </FormField>

            <FormField
              label="Country"
              helperText="Select your country of residence"
            >
              <Select
                value={formState.country}
                onChange={(e) => handleChange('country', e.target.value)}
              >
                <option value="">Select a country</option>
                <option value="us">United States</option>
                <option value="uk">United Kingdom</option>
                <option value="ca">Canada</option>
              </Select>
            </FormField>

            <FormField
              label="Bio"
              helperText="Tell us about yourself"
            >
              <TextArea
                placeholder="I am..."
                value={formState.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                rows={3}
              />
            </FormField>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="${typography.h4} ${textColors.primary} mb-3">Code Example</h3>
          <CodeBlock code={`import { FormField, Input } from '../design-system';

<FormField
  label="Email Address"
  error={errors.email}
  helperText="We'll never share your email"
  required
>
  <Input 
    type="email" 
    value={email} 
    onChange={handleChange}
    error={errors.email}
  />
</FormField>`} />
        </div>
      </section>
    </div>
  );
};

export default InputShowcase;
